import { Injectable } from '@angular/core';

export interface JwtPayload {
  UserId?: string;
  ArabicName?: string;
  EnglishName?: string;
  MDrPm?: string | string[]; // Roles/permissions
  exp?: number;
  iat?: number;
  Role?: string;
  CompanyId?: string;
  SeasonId?: string;
  email?: string;
  [key: string]: unknown;
}

@Injectable({
  providedIn: 'root'
})
export class JwtHelperService {
  /**
   * Decode JWT token without verification
   */
  decodeToken(token: string): JwtPayload | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }

      const payload = parts[1];
      const decoded = this.base64UrlDecode(payload);
      return JSON.parse(decoded);
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }

  /**
   * Check if token is expired
   */
  isTokenExpired(token: string, offsetSeconds: number = 0): boolean {
    const decoded = this.decodeToken(token);
    if (!decoded || !decoded.exp) {
      return true;
    }

    const expirationDate = decoded.exp * 1000;
    const currentTime = Date.now() + (offsetSeconds * 1000);

    return expirationDate < currentTime;
  }

  /**
   * Get token expiration date
   */
  getTokenExpirationDate(token: string): Date | null {
    const decoded = this.decodeToken(token);
    if (!decoded || !decoded.exp) {
      return null;
    }

    return new Date(decoded.exp * 1000);
  }

  /**
   * Get roles from token MDrPm property
   */
  getRolesFromToken(token: string): string[] {
    const decoded = this.decodeToken(token);
    const permissions = decoded?.MDrPm;

    if (!permissions || (Array.isArray(permissions) && permissions.length === 0)) {
      return [];
    }

    const roles = typeof permissions === 'string' ? [permissions] : permissions;
    return roles.filter(role => role);
  }

  /**
   * Get UserId from token
   */
  getUserIdFromToken(token: string): string | null {
    const decoded = this.decodeToken(token);
    return decoded?.UserId || null;
  }

  /**
   * Get user name from token (ArabicName or EnglishName based on language)
   */
  getUserNameFromToken(token: string, language: string = 'en'): string | null {
    const decoded = this.decodeToken(token);
    if (!decoded) {
      return null;
    }
    return language === 'ar' ? (decoded.ArabicName || null) : (decoded.EnglishName || null);
  }

  /**
   * Get email from token
   */
  getEmailFromToken(token: string): string | null {
    const decoded = this.decodeToken(token);
    return decoded?.email || null;
  }

  /**
   * Check if token has specific role
   */
  hasRole(token: string, role: string): boolean {
    const roles = this.getRolesFromToken(token);
    return roles.includes(role);
  }

  /**
   * Check if token has any of the specified roles
   */
  hasAnyRole(token: string, requiredRoles: string[]): boolean {
    const roles = this.getRolesFromToken(token);
    return requiredRoles.some(role => roles.includes(role));
  }

  /**
   * Check if token has all of the specified roles
   */
  hasAllRoles(token: string, requiredRoles: string[]): boolean {
    const roles = this.getRolesFromToken(token);
    return requiredRoles.every(role => roles.includes(role));
  }

  /**
   * Base64 URL decode
   */
  private base64UrlDecode(str: string): string {
    let output = str.replace(/-/g, '+').replace(/_/g, '/');

    switch (output.length % 4) {
      case 0:
        break;
      case 2:
        output += '==';
        break;
      case 3:
        output += '=';
        break;
      default:
        throw new Error('Invalid base64url string');
    }

    try {
      return decodeURIComponent(
        atob(output)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
    } catch {
      return atob(output);
    }
  }
}
